// Webpack 4 does not understand the package `exports` map used by
// keycloak-js 26, so resolve its browser adapter entry explicitly.
import Keycloak from 'keycloak-js/lib/keycloak.js';
import axios from 'axios';
import Cookies from 'js-cookie';
import Auth from '@groovepages/gd-lib/auth';
import { logoutFromGroove, clearGrooveAuthCookies } from '../../../shared/session-logout.mjs';
import { describeKeycloakLoginFailure, publishBootstrap } from '../../../shared/bootstrap-failure.mjs';
import {
  GROOVE_USER_COOKIE,
  applySharedUserToken,
  clearSharedUser,
  readSharedUserCookie,
  readStoredUser,
  writeSharedUser
} from './sharedUserSession.mjs';

class KeycloakService {
  constructor() {
    this.keycloak = null;
    this.initialized = false;
    this.loginFailure = null;
  }

  async init() {
    if (this.initialized) {
      return this.keycloak.authenticated;
    }

    // Initialize Keycloak instance
    this.keycloak = new Keycloak({
      url: window.gdEnv?.VUE_APP_KEYCLOAK_URL || 'https://auth.groovetech.io/auth',
      realm: window.gdEnv?.VUE_APP_KEYCLOAK_REALM || 'gd-apis-live',
      clientId: window.gdEnv?.VUE_APP_KEYCLOAK_CLIENT_ID || 'app-groovecm'
    });

    try {
      const authenticated = await this.keycloak.init({
        onLoad: 'check-sso',
        // Vue Router uses the URL fragment for routes (for example, #/login).
        // Keep the OIDC response out of that fragment so Keycloak does not
        // append its `iss` parameter to the callback route on every SSO check.
        responseMode: 'query',
        // Keep PKCE explicit even though Keycloak JS 26 enables S256 by default.
        pkceMethod: 'S256',
        // Matches the 2026-09-26 emergency hotfix (app.hotfix-rb2.js): the
        // login-status iframe and silent SSO check are disabled for the dashboard.
        checkLoginIframe: false,
        checkLoginIframeInterval: 30,
        enableLogging: true
      });

      if (authenticated) {
        console.log('User is authenticated');
        this.setupTokenRefresh();
        this.updateAxiosHeaders();
        // Load complete user profile with backend data
        await this.loadCompleteUserProfile();
      } else {
        console.log('User is not authenticated');
        // Clear any stale data from localStorage if SSO session ended.
        // Only a Keycloak gd-lib cookie is removed; other sessions stay.
        this.clearLocalStorage();
        this.clearKeycloakSharedCookie();
      }

      this.initialized = true;

      // Setup event handlers
      this.keycloak.onTokenExpired = () => {
        console.log('Token expired, refreshing...');
        this.refreshToken();
      };

      this.keycloak.onAuthSuccess = () => {
        console.log('Auth success');
        this.updateAxiosHeaders();
      };

      this.keycloak.onAuthError = (error) => {
        console.error('Auth error:', error);
      };

      this.keycloak.onAuthRefreshSuccess = () => {
        console.log('Token refresh success');
        this.updateAxiosHeaders();
      };

      this.keycloak.onAuthRefreshError = () => {
        console.error('Token refresh error');
        this.login();
      };

      this.keycloak.onAuthLogout = () => {
        console.log('Auth logout');
        this.clearLocalStorage();
        // Same cleanup as logout(): gd-lib sentinel, then expire the auth cookies.
        this.clearSharedSession();
      };

      return authenticated;
    } catch (error) {
      console.error('Failed to initialize Keycloak:', error);
      this.initialized = false;
      return false;
    }
  }

  setupTokenRefresh() {
    // Refresh token every 60 seconds
    setInterval(() => {
      this.keycloak.updateToken(70).then((refreshed) => {
        if (refreshed) {
          console.log('Token refreshed');
          this.updateAxiosHeaders();
        }
      }).catch(() => {
        console.error('Failed to refresh token');
        this.login();
      });
    }, 60000);
  }

  updateAxiosHeaders() {
    if (this.keycloak.token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${this.keycloak.token}`;
      // Also set for Groove API compatibility
      axios.defaults.headers.common['x-auth-provider'] = 'keycloak';

      // Store token in localStorage for persistence
      localStorage.setItem('keycloak_token', this.keycloak.token);
      localStorage.setItem('keycloak_refresh_token', this.keycloak.refreshToken);
      localStorage.setItem('keycloak_id_token', this.keycloak.idToken);

      // Groove Member and GrooveVideo send Auth.authenticatedUser().token.
      // That helper prefers the gd-lib cookie over localStorage.user.
      // A blocked cookie jar must not look like a failed token refresh.
      try {
        const subject = this.keycloak.tokenParsed?.sub;
        const stored = readStoredUser(localStorage);
        // A leftover profile from someone else must not ride along on this token.
        const profile = subject && stored.kc_sub === subject ? stored : {};
        const user = applySharedUserToken(profile, this.keycloak.token);
        writeSharedUser(localStorage, Cookies, user);
      } catch (error) {
        console.error('Failed to publish the shared Groove session:', error);
      }
    }
  }

  async loadUserProfile() {
    try {
      const profile = await this.keycloak.loadUserProfile();

      // Transform Keycloak profile to match existing user structure
      const user = {
        id: profile.id || profile.sub,
        email: profile.email,
        name: profile.firstName && profile.lastName
          ? `${profile.firstName} ${profile.lastName}`
          : profile.username,
        username: profile.username,
        email_verified: profile.emailVerified,
        auth_keycloak: true,
        roles: this.getRoles(),
        profile: profile
      };

      // Store user in localStorage for app compatibility
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('keycloak_user', JSON.stringify(profile));

      return user;
    } catch (error) {
      console.error('Failed to load user profile:', error);
      return null;
    }
  }

  async loadCompleteUserProfile() {
    try {
      // Make sure we have a token
      if (!this.keycloak || !this.keycloak.token) {
        console.error('No Keycloak token available');
        return null;
      }

      // Call the new backend endpoint with the Keycloak token
      const response = await axios.post('/groovedigital/auth/keycloak-token-login', {}, {
        headers: {
          'Authorization': `Bearer ${this.keycloak.token}`
        }
      });

      if (response.data) {
        // The access token Groove Member sends is the current Keycloak token,
        // and auth_keycloak must be 1 or Member sends x-auth-provider: auth0.
        const userData = applySharedUserToken({
          ...response.data,
          kc_sub: this.keycloak.tokenParsed?.sub
        }, this.keycloak.token);
        delete userData.refresh_token;
        delete userData.refresh_expires_in;

        try {
          writeSharedUser(localStorage, Cookies, userData);
        } catch (error) {
          console.error('Failed to publish the shared Groove session:', error);
        }

        // Store Keycloak profile separately if needed
        if (this.keycloak.profile) {
          localStorage.setItem('keycloak_user', JSON.stringify(this.keycloak.profile));
        }

        this.loginFailure = null;
        publishBootstrap({ loginFailure: null });
        console.log('Loaded complete user profile from backend');
        return userData;
      }

      throw new Error('No data returned from backend');

    } catch (error) {
      console.error('Failed to load complete user profile:', error);
      // Keep the Keycloak session. A fake local user here is what let the next
      // 401 clear Vuex and unmount the dashboard into an empty document.
      this.loginFailure = describeKeycloakLoginFailure(error);
      publishBootstrap({ loginFailure: this.loginFailure });
      return null;
    }
  }

  getRoles() {
    if (!this.keycloak.tokenParsed) {
      return [];
    }

    const roles = [];

    // Get realm roles
    if (this.keycloak.tokenParsed.realm_access?.roles) {
      roles.push(...this.keycloak.tokenParsed.realm_access.roles);
    }

    // Get client roles
    const clientId = this.keycloak.clientId;
    if (this.keycloak.tokenParsed.resource_access?.[clientId]?.roles) {
      roles.push(...this.keycloak.tokenParsed.resource_access[clientId].roles);
    }

    return roles;
  }

  login(options = {}) {
    const redirectUri = options.redirectUri || window.location.href;
    return this.keycloak.login({
      redirectUri,
      ...options
    });
  }

  logout(options = {}) {
    // logoutFromGroove calls Auth.logout() (gd-lib sentinel + local user
    // removal) and expires the same auth cookies, including the shared user.
    return logoutFromGroove({
      browser: window, auth: Auth, axios, keycloak: this.keycloak,
      clearAppSession: options.clearAppSession
    });
  }

  clearSharedSession() {
    clearSharedUser(localStorage, Cookies);
    clearGrooveAuthCookies(window);
    delete axios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['x-auth-provider'];
  }

  // Sign-in came back logged out. Remove the shared cookie only when it is
  // the Keycloak record this app published (auth_keycloak === 1).
  clearKeycloakSharedCookie() {
    const record = readSharedUserCookie(Cookies);
    if (!record || record.auth_keycloak !== 1) return;
    clearSharedUser(localStorage, Cookies);
    clearGrooveAuthCookies(window, [GROOVE_USER_COOKIE]);
  }

  register(options = {}) {
    const redirectUri = options.redirectUri || window.location.href;
    return this.keycloak.register({
      redirectUri,
      ...options
    });
  }

  async refreshToken(options = {}) {
    const redirectOnFail = !options || options.redirectOnFail !== false;
    try {
      const refreshed = await this.keycloak.updateToken(5);
      if (refreshed) {
        console.log('Token refreshed');
        this.updateAxiosHeaders();
      }
      return true;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      if (redirectOnFail) this.login();
      return false;
    }
  }

  isAuthenticated() {
    return this.keycloak?.authenticated || false;
  }

  hasRealmRole(role) {
    return this.keycloak?.hasRealmRole(role) || false;
  }

  hasResourceRole(role, resource) {
    return this.keycloak?.hasResourceRole(role, resource) || false;
  }

  getToken() {
    return this.keycloak?.token || null;
  }

  getRefreshToken() {
    return this.keycloak?.refreshToken || null;
  }

  getIdToken() {
    return this.keycloak?.idToken || null;
  }

  getTokenParsed() {
    return this.keycloak?.tokenParsed || null;
  }

  getUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  clearLocalStorage() {
    localStorage.removeItem('keycloak_token');
    localStorage.removeItem('keycloak_refresh_token');
    localStorage.removeItem('keycloak_id_token');
    localStorage.removeItem('keycloak_user');
    localStorage.removeItem('user');
    localStorage.removeItem('token');

    // Clear axios headers
    delete axios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['x-auth-provider'];
  }

  // Compatibility method for existing auth checks
  authenticatedUser() {
    if (!this.isAuthenticated()) {
      return null;
    }
    return this.getUser();
  }
}

// Export singleton instance
const keycloakService = new KeycloakService();
export default keycloakService;
